/**
 * The push transport: SSE connection lifecycle + the ordered ingest (sse-transport-swap design).
 * The server now walks display pauses itself and pushes one message per pause-step; this module
 * owns the seq-gated buffer, sequential application, gap-timeout resync, and the dev chain gate
 * that used to live on api.js's retired `/continue` chain.
 *
 * Wiring direction (no cycle): api.js imports this module one-way (ensureOpen/close/noteApplied/
 * claimSeq); the reverse edge — applying a message, or triggering a resync fetch — goes through
 * callback injection (initPushStream), wired in boot.js with api.js's own applyServerState/
 * fetchState — a direct import back here would cycle (api.js already imports push-stream.js).
 *
 * Visibility resync (sse-silent-connection-death-no-recovery): a backgrounded phone tab or a NAT
 * idle timeout can kill the underlying TCP connection without the browser ever noticing — the
 * `EventSource` keeps reading `OPEN` forever, so neither `ensureOpen`'s CLOSED-recreate nor
 * `handleError`'s terminal-close resync ever fire, and total silence buffers nothing so the gap
 * timeout never arms either. `document.visibilitychange` (hidden→visible) is the one signal that
 * survives a connection dying invisibly: the moment the player looks back at the board, both a
 * force-reconnect (drop the possibly-zombie EventSource, open a fresh one) AND a `resyncFn()` call
 * fire. Both halves are load-bearing — a resync alone leaves the dead pipe open (a zombie still
 * reads OPEN, so `ensureOpen` keeps no-op'ing on it), and a reconnect alone waits up to
 * GAP_TIMEOUT_MS to catch up (the fresh connection's own snapshot lands ahead of the still-current
 * watermark, so it buffers rather than applies — see `ingest`).
 *
 * The force-reconnect deliberately does NOT go through `close()`: it only tears down and replaces
 * the `EventSource`, leaving the watermark/buffer/claim gate untouched. `close()`'s full reset is
 * for an intentional clear (quit) or a game that's genuinely gone — a visibility flip on a
 * connection that never actually died (plain desktop tab-switching, the common case) has no reason
 * to discard a perfectly valid watermark. The invariant `noteApplied`/`rebaseWatermark` already
 * document still holds — the watermark only ever moves backward via `rebaseWatermark` — but firing
 * `forceReconnect` and `resyncFn()` concurrently (rather than `resyncFn()` alone, every other
 * caller's shape) opens one known, accepted race: if the fresh connection's own connect-snapshot
 * applies first (a genuinely newer seq, via the ordinary seq-gated `ingest` path) and the in-flight
 * `resyncFn()` fetch then resolves with an OLDER seq (the game advanced between the fetch's
 * server-side read and the snapshot's emission), `rebaseWatermark` — deliberately unconditional,
 * "the one deliberate unconditional re-base" per the sse-transport-swap design's seq protocol —
 * moves the watermark backward past state that had already legitimately applied, so the board
 * regresses to the older fetched state (the SAME state also re-anchors waiting-state.js's own
 * idle clock in lockstep — see its `continueWait` doc for that side). Recovery is NOT automatic:
 * `rebaseWatermark` clears the buffer and gap timer, so nothing is left armed — the board stays on
 * the regressed state until either a NEW state-advancing action produces a fresh SSE message
 * (which the ordinary ingest/gap-timeout path then catches up on normally) or another visibility
 * flip past the throttle fires a further resync; in an idle off-turn wait, that first trigger may
 * be a while off. This is the SAME trade-off `rebaseWatermark`'s own bidirectional nature already
 * accepts for its other callers (gap-timeout, terminal-close resync) — visibility resync is the
 * first caller to make it a routine per-tab-return occurrence rather than an exceptional one, not
 * a new class of race (review round, sse-silent-connection-death-no-recovery). A real fix needs a
 * freshness fence across `resyncFn`'s two concurrent server round-trips, which touches
 * `rebaseWatermark`'s other callers too — left as a follow-up rather than folded into this PR.
 */
import { isChainHalted, isIntentionalClear } from "./state.js";

/** @type {EventSource|null} */
let source = null;

/** The seq gate's watermark — never moves backward except via rebaseWatermark's deliberate reset. */
let lastAppliedSeq = 0;

/** The claim gate's high-water mark (multiplayer per-seat SSE design § (b)): the highest seq any
 * caller has claimed via claimSeq, whether or not its apply has finished yet. Distinct from
 * lastAppliedSeq — that one only advances on a *completed* apply (noteApplied); this one advances
 * the instant a caller wins the claim, closing the window a response and its own stream duplicate
 * could otherwise both pass a plain "> watermark" check and apply concurrently. */
let claimedSeq = 0;

/** Ahead-of-watermark stream messages, keyed by seq, waiting for their predecessor. */
const buffer = new Map();

/** The shared gap-timeout handle — armed while the buffer holds anything, cleared once it's empty. */
let gapTimer = null;

/** A buffered message waiting this long for its predecessor triggers a resync fetch.
 * play-test: tune against real cross-channel skew (design § open questions). */
const GAP_TIMEOUT_MS = 2000;

/** The game id the most recent `ensureOpen` call opened a stream for — recalled purely so the
 * visibilitychange handler can force a reconnect on its own, without new boot.js wiring passing
 * it a gameId. Not a general-purpose handle (api.js owns that); this module still takes gameId as
 * a caller-supplied param everywhere else, per the header's no-cycle reasoning. */
let currentGameId = null;

/** The last visibility-triggered resync's timestamp — guards a resync storm on rapid tab-flipping.
 * play-test: a few seconds is plenty to absorb a flurry of flips while still resyncing promptly
 * on a genuine return to the tab. */
const VISIBILITY_RESYNC_THROTTLE_MS = 5000;
let lastVisibilityResyncAtMs = 0;

/** @type {((data: object) => Promise<void>)|null} Injected: applies one message via
 * api.js's applyServerState-based funnel. */
let applyFn = null;

/** @type {(() => void)|null} Injected: api.js's fetchState — the resync/recovery authority. */
let resyncFn = null;

/** @type {((data: object) => Promise<void>|undefined)|null} Dev-only pause gate (?paused=true),
 * wired by the lazy-imported dev/chain-pause.js — held before applying the next buffered message
 * so a probe can instrument the at-rest board (the gate paces presentation, not computation: the
 * server has already walked to rest by the time any message is buffered here). */
let chainGate = null;

/** Promise-chain lock serializing message application — the dispatch.js#tail idiom, one level up:
 * each apply is awaited before the next dequeues. */
let applyChain = Promise.resolve();

/** Guards the terminal-close resync to exactly one fetch per dead connection — no retry loop. */
let terminalResyncFired = false;

/** Wire the funnel applier and the resync authority — called once from boot.js. */
export function initPushStream(onApply, onResync) {
  applyFn = onApply;
  resyncFn = onResync;
}

/** Wire (or clear, with null) the dev pause gate — dev/chain-pause.js's new home. */
export function initChainGate(fn) {
  chainGate = fn;
}

/**
 * Opens the stream for `gameId` if not already open — idempotent, like slice 1. Also recreates the
 * connection when the prior one closed terminally (`readyState === CLOSED`, amendment over slice
 * 1): an `EventSource` never retries past an HTTP error response (a deploy's Caddy 502 kills it
 * for good), so leaving `source` non-null there would strand `ensureOpen()` as a permanent no-op.
 * `gameId` is a caller-supplied parameter, not read from api.js's handle directly — api.js already
 * imports this module one-way (see the header), so importing api.js back here to read the handle
 * would cycle.
 */
export function ensureOpen(gameId) {
  currentGameId = gameId;
  if (source && source.readyState !== EventSource.CLOSED) return;
  terminalResyncFired = false;
  source = new EventSource(`/api/games/${gameId}/events`);
  source.onmessage = handleMessage;
  source.onerror = handleError;
}

/**
 * Closes the stream (if open) and resets all ingest state — an intentional clear (quit) or a
 * fetched state with no live game. A future reconnect starts from a fresh snapshot and a fresh
 * watermark, so there is nothing to preserve across a close.
 */
export function close() {
  if (source) {
    source.onmessage = null;
    source.onerror = null;
    source.close();
    source = null;
  }
  lastAppliedSeq = 0;
  claimedSeq = 0;
  buffer.clear();
  clearGapTimer();
  terminalResyncFired = false;
  currentGameId = null;
  lastVisibilityResyncAtMs = 0;
}

/** The tracked seq watermark — exported for tests and for callers that only need to read it. */
export function getWatermark() {
  return lastAppliedSeq;
}

/** True when no ahead-of-watermark message is waiting on its predecessor — the card-queue rest
 * drain's gate (api.js#applyServerState): a non-empty buffer means the walk isn't done landing
 * yet, so a queued card mustn't race ahead of it. */
export function isBufferEmpty() {
  return buffer.size === 0;
}

/**
 * The claim gate api.js's funnel consults (multiplayer per-seat SSE design § (b)): a plain "seq
 * exceeds the watermark" check is not a reservation — a response and its own stream duplicate can
 * both pass it and apply concurrently once action transitions started reaching the stream too.
 * This is synchronous and mutating: true iff `seq` exceeds both the applied watermark and the
 * highest already-claimed seq, in which case it claims `seq` (advances claimedSeq) before
 * returning: the first of two racing callers to reach this wins, the second's claim fails and it
 * takes the existing bookkeeping-only (finishPromptCommit) path applyServerState already had for a
 * stale/behind seq. A claimed-but-failed apply recovers exactly as an unclaimed one does — it never
 * calls noteApplied, so its successor (the next real seq) still exceeds claimedSeq and claims fine;
 * the gap is what the buffer/gap-timeout resync already covers.
 */
export function claimSeq(seq) {
  if (seq <= lastAppliedSeq || seq <= claimedSeq) return false;
  claimedSeq = seq;
  return true;
}

/**
 * Records a successful application: advances the watermark, drops now-stale buffered entries
 * (a response's forward jump can outrun stream messages already sitting in the buffer), and
 * releases the next buffered message if the advance made it due. Called by the funnel after
 * EVERY successful apply, response or stream-sourced alike.
 *
 * Monotonic: the watermark only ever moves forward here. An apply that passed the seq gate and
 * then awaited its render can be overtaken by a resync's rebaseWatermark — its own (now lower)
 * seq landing afterwards must not drag the watermark back below the resync, or the dropped-as-
 * stale stream messages between them would apply after all. rebaseWatermark stays the one
 * deliberate bidirectional mover.
 *
 * Buffer-nonempty ⇒ timer-armed: entries can outlive a gap-timeout firing (its resync fetch bails
 * while dispatch's tail is occupied — renderFetchedState's tail guard, which covers a burst queued
 * behind the round-start replay as well as one mid-mutation), so re-arm here whenever entries
 * remain; the work that caused the bail always ends in another noteApplied, which is what re-arms.
 * That re-arm is what makes the bail free: the skipped resync never rebased the watermark, so the
 * entries it would have dropped are still here to apply in order.
 */
export function noteApplied(seq) {
  if (seq > lastAppliedSeq) lastAppliedSeq = seq;
  for (const bufferedSeq of buffer.keys()) {
    if (bufferedSeq <= lastAppliedSeq) buffer.delete(bufferedSeq);
  }
  if (buffer.size === 0) clearGapTimer();
  drainNext();
  if (buffer.size > 0) armGapTimeout();
}

/**
 * The deliberate unconditional re-base `fetchState` performs (via `renderFetchedState`): unlike
 * `noteApplied`, this may move the watermark in EITHER direction (a fresh HTTP session restarts
 * the counter below the client's prior watermark) and discards the whole buffer rather than
 * pruning by comparison — a backward rebase would otherwise misread old-game entries above the
 * new low watermark as legitimate future messages of the new game. Resets the claim gate
 * (claimedSeq) alongside the watermark, to the same value — a claim from before the rebase must
 * not outlive it and block a legitimate post-rebase seq from claiming.
 */
export function rebaseWatermark(seq) {
  lastAppliedSeq = seq;
  claimedSeq = seq;
  buffer.clear();
  clearGapTimer();
}

function handleMessage(event) {
  const data = JSON.parse(event.data);
  if (typeof data.seq !== "number") return;
  ingest(data);
}

/**
 * Terminal-close resync (amendment): an `onerror` with `readyState === CLOSED` means the browser
 * gave up for good (an HTTP error response, not a network blip — those the browser retries
 * itself). A stream that died mid-walk would otherwise leave the board hanging: no further
 * messages arrive, so the gap-timeout never fires (it needs a buffered message ahead). One
 * resync fetch — not a retry loop — brings the board to rest; if the resync itself fails
 * (mid-deploy), `recoverFromApiError` is the existing net.
 */
function handleError() {
  if (source?.readyState !== EventSource.CLOSED) return;
  if (terminalResyncFired) return;
  terminalResyncFired = true;
  resyncFn?.();
}

/**
 * Drops the current connection (whatever its readyState — a zombie still reads OPEN, so this
 * bypasses ensureOpen's idempotent no-op gate) and opens a fresh one against the same game.
 * Deliberately narrower than close() — see the module header's "Visibility resync" section for why
 * ingest state survives this.
 */
function forceReconnect() {
  if (source) {
    source.onmessage = null;
    source.onerror = null;
    source.close();
    source = null;
  }
  ensureOpen(currentGameId);
}

/**
 * The visibilitychange resync (module header). Gates on the tab actually becoming visible (not
 * every flip — a hide needs no reaction) and on a stream being "supposed to be live" (source !=
 * null naturally excludes the start screen and any state after an intentional close()). The
 * throttle is keyed off this handler's own last fire, not per-tab-hide duration, so a rapid flurry
 * of flips only ever resyncs once per window regardless of how many hidden→visible edges it
 * contains.
 */
function handleVisibilityChange() {
  if (document.visibilityState !== "visible") return;
  if (source == null) return;
  const now = Date.now();
  if (now - lastVisibilityResyncAtMs < VISIBILITY_RESYNC_THROTTLE_MS) return;
  lastVisibilityResyncAtMs = now;
  forceReconnect();
  resyncFn?.();
}

// Guarded like api.js's localStorage read: this module loads transitively in plain-Node vitest
// runs with no jsdom (e.g. board-layout.spatial-phase.test.js -> ... -> api.js -> push-stream.js),
// where `document` doesn't exist — the module import itself must not crash on that path.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", handleVisibilityChange);
}

function ingest(data) {
  const seq = data.seq;
  if (seq <= lastAppliedSeq) return; // duplicate or behind — silent drop
  if (seq === lastAppliedSeq + 1) {
    enqueueApply(data);
  } else {
    buffer.set(seq, data);
    armGapTimeout();
  }
}

function enqueueApply(data) {
  // The catch keeps one rejected apply from poisoning the whole chain (every later .then would
  // silently skip). Recovery needs no more than the log: the failed apply never called
  // noteApplied, so its successor buffers as a gap and the gap-timeout resync re-syncs the board.
  applyChain = applyChain
    .then(() => runApply(data))
    .catch((e) => {
      console.error("[push-stream] stream apply failed:", e);
    });
}

async function runApply(data) {
  if (isChainHalted() || isIntentionalClear()) return;
  if (chainGate) await chainGate(data);
  // The hold is unbounded — re-check after it releases (a halt or clear may have landed while it sat).
  if (isChainHalted() || isIntentionalClear()) return;
  await applyFn?.(data);
}

/** Releases the buffered message that is now exactly next-in-line, if any — called after every
 * noteApplied, since a forward-jumping response can make a previously-out-of-order entry due. */
function drainNext() {
  const next = buffer.get(lastAppliedSeq + 1);
  if (!next) return;
  buffer.delete(lastAppliedSeq + 1);
  enqueueApply(next);
}

function armGapTimeout() {
  if (gapTimer != null) return; // one shared timer for the whole buffer (approximation, § design)
  gapTimer = setTimeout(() => {
    gapTimer = null;
    resyncFn?.();
  }, GAP_TIMEOUT_MS);
}

function clearGapTimer() {
  if (gapTimer != null) {
    clearTimeout(gapTimer);
    gapTimer = null;
  }
}
